// TransferCell.js

import Spring from "@components/Spring";
import { useThemeProvider } from "@contexts/themeContext";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import ApiService from "../../network/ApiService";
import styles from "./TransferCell.module.scss";

// Function to format the nationality string for German characters
const formatNationality = (nationality = "") => {
  return nationality
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/ /g, "")
    .replace(/[^a-z]/g, ""); // Remove any non-alphabetical characters
};

const getEligibilityClass = (eligibility) => {
  switch (eligibility) {
    case "Spielberechtigt":
      return styles.eligible;
    case "Gesperrt":
      return styles.suspended;
    case "Warten":
      return styles.waiting;
    default:
      return "";
  }
};

const PENDING_IMAGE =
  "https://firebasestorage.googleapis.com/v0/b/oekfbbucket.appspot.com/o/adminfiles%2FpendingPlayer.png?alt=media&token=b504ecd3-3c5d-4f88-b629-e901c51f5bb4";

const FALLBACK_PLAYER_IMAGE = PENDING_IMAGE;

const TransferCell = ({ player, team, refreshTransferWindow }) => {
  const { direction } = useThemeProvider();
  const [requestSent, setRequestSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestError, setRequestError] = useState("");
  const apiService = useMemo(() => new ApiService(), []);

  const formattedNationality = formatNationality(player.nationality);

  const initialImgSrc =
    player.eligibility === "Warten" ? PENDING_IMAGE : player.image;

  const [imgSrc, setImgSrc] = useState(initialImgSrc);
  const [flagSrc, setFlagSrc] = useState(
    `https://www.zeitzonen.de/build/images/flags/${formattedNationality}.svg`
  );

  useEffect(() => {
    // update image sources if player changes
    setImgSrc(player.eligibility === "Warten" ? PENDING_IMAGE : player.image || FALLBACK_PLAYER_IMAGE);
    setFlagSrc(
      `https://www.zeitzonen.de/build/images/flags/${formatNationality(
        player.nationality
      )}.svg`
    );
  }, [player]);

  const handleTransferRequest = async () => {
    if (!team?.id || !player?.id) {
      console.error("Team or player data is missing");
      return;
    }

    setSubmitting(true);
    setRequestError("");

    const transferWindowIsOpen = await refreshTransferWindow();
    if (!transferWindowIsOpen) {
      setRequestError("Das Transferfenster wurde inzwischen geschlossen.");
      setSubmitting(false);
      return;
    }

    // Display fields are included for compatibility; the server validates the IDs.
    const body = {
      player: player.id,
      team: team.id,
      teamName: team.team_name ?? team.name ?? "NO NAME",
      playerName: player.name,
      origin: team.id,
      teamImage: team.logo ?? team.image ?? "",
      playerImage: player.image ?? "",
    };

    try {
      const response = await apiService.post(`app/transfer/create`, body);
      if (response) {
        setRequestSent(true);
      } else {
        console.error("Failed to send transfer request", response);
      }
    } catch (error) {
      console.error("Error sending transfer request:", error);
      setRequestError(error.message || "Die Transferanfrage konnte nicht gesendet werden.");
      await refreshTransferWindow();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Spring className={`card ${styles.card} ${styles[direction]}`}>
      <div className={styles.wrapper}>
        <div className={styles.textContent}>
          <div className={styles.numberBox}>
            <span className={styles.playerNumber}>
              {player.number ? player.number : "?"}
            </span>
          </div>
          <div className={styles.playerInfo}>
            <h2 className={styles.playerName}>{player.name}</h2>
            <p className={styles.playerSID}>SID: {player.sid}</p>
            <div className={styles.nationalityRow}>
              {flagSrc ? (
                <img
                  src={flagSrc}
                  alt={player.nationality}
                  className={styles.flagImage}
                  onError={() => setFlagSrc("")}
                  loading="lazy"
                />
              ) : (
                <span aria-hidden="true">🌐</span>
              )}
              <span>{player.nationality}</span>
            </div>
            <p
              className={`${styles.playerPosition} ${getEligibilityClass(
                player.eligibility
              )}`}
            >
              {player.eligibility}
            </p>
            <p className={styles.playerPosition}>{player.position}</p>

            <button
              className={styles.transferButton}
              onClick={handleTransferRequest}
              disabled={requestSent || submitting}
              style={requestSent || submitting ? { opacity: 0.4 } : {}}
            >
              {submitting
                ? "Transferfenster wird geprüft ..."
                : requestSent
                ? "Transfer Anfrage Gesendet"
                : "Transfer Anfrage senden"}
            </button>
            {requestError && <p className={styles.requestError}>{requestError}</p>}
          </div>
        </div>
      </div>
      <div className={styles.imageWrapper}>
        <LazyLoadImage
          className={styles.playerImage}
          src={imgSrc}
          alt={player.name}
          onError={() => setImgSrc(FALLBACK_PLAYER_IMAGE)}
          effect="opacity"
        />
      </div>
    </Spring>
  );
};

TransferCell.propTypes = {
  player: PropTypes.shape({
    number: PropTypes.string,
    name: PropTypes.string.isRequired,
    nationality: PropTypes.string.isRequired,
    eligibility: PropTypes.string.isRequired,
    position: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    sid: PropTypes.string,
    team: PropTypes.string, // if present on player object
  }).isRequired,
  team: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    team_name: PropTypes.string, // some lists use team_name
    image: PropTypes.string,
    logo: PropTypes.string,
  }),
  refreshTransferWindow: PropTypes.func.isRequired,
};

export default TransferCell;
